import { useState } from "react";
import axios from "axios";
import { getPreviousMonthValue } from "../../components/admin/MonthSelect";

const initialBillingFilters = {
  customer_code: "",
  selected_customer_codes: [],
  user_email: "",
  month: getPreviousMonthValue(),
  format: "excel",
};

export const getBillingDownloadFilename = (response, fallbackName) => {
  const headers = response?.headers || {};
  const disposition = headers["content-disposition"]
    || headers["Content-Disposition"]
    || (typeof headers.get === "function" ? headers.get("content-disposition") : "");
  const encodedMatch = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch) {
    return decodeURIComponent(encodedMatch[1]);
  }
  return fallbackName;
};

export default function useAdminBilling({ apiBaseUrl, setLoading }) {
  const [billingFilters, setBillingFilters] = useState(initialBillingFilters);

  const downloadBillingResponse = async (params, format, fallbackCustomerCode) => {
    const response = await axios.get(`${apiBaseUrl}/admin/export/billing?${params.toString()}`, {
      responseType: "blob"
    });

    const contentType = response.headers["content-type"];
    if (contentType && contentType.includes("application/json")) {
      const text = await response.data.text();
      const errorData = JSON.parse(text);
      throw new Error(errorData.detail || "导出失败");
    }

    const blob = new Blob([response.data], {
      type: format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const customerCode = fallbackCustomerCode || billingFilters.customer_code || "all";
    const month = billingFilters.month || "all";
    const fallbackFilename = `billing_${customerCode}_${month}.${format === "pdf" ? "pdf" : "xlsx"}`;
    link.download = getBillingDownloadFilename(response, fallbackFilename);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportBilling = async () => {
    const params = new URLSearchParams();
    if (billingFilters.customer_code) {
      params.append("customer_code", billingFilters.customer_code);
    }
    if (billingFilters.user_email) {
      params.append("user_email", billingFilters.user_email);
    }
    if (billingFilters.month) {
      params.append("month", billingFilters.month);
    }

    const format = billingFilters.format || "excel";
    params.append("format", format);

    setLoading(true);
    try {
      const selectedCustomerCodes = billingFilters.selected_customer_codes || [];
      if (selectedCustomerCodes.length > 0) {
        for (const customerCode of selectedCustomerCodes) {
          params.set("customer_code", customerCode);
          params.delete("user_email");
          await downloadBillingResponse(params, format, customerCode);
        }
        alert(`批量导出成功：${selectedCustomerCodes.length} 个客户`);
        return;
      }

      await downloadBillingResponse(params, format);
      alert("导出成功！");
    } catch (error) {
      console.error("导出失败:", error);
      let errorMessage = "请重试";

      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${error.response?.status || "Error"}: ${error.message || "请重试"}`;
        }
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert("导出失败: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    billingFilters,
    handleExportBilling,
    setBillingFilters,
  };
}
